import { useEffect, useRef, useState } from 'react';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import './BlSelect.css';

/**
 * Dropdown personnalisé (remplace le <select> natif).
 * - value / onChange : comme un select classique (string)
 * - options : tableau de strings OU d'objets { value, label }
 * - placeholder : libellé de l'option "toutes" (value === '')
 * - icon : icône affichée à gauche
 */
function BlSelect({ value, onChange, options = [], placeholder = 'Sélectionner', icon = null, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Normalise les options : string -> { value, label }
  const items = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );
  const selectedLabel = items.find((it) => it.value === value)?.label || value;

  // Ferme au clic extérieur + touche Échap
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className={`bl-select ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="bl-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {icon && <span className="bl-select-ic">{icon}</span>}
        <span className={`bl-select-label ${value ? '' : 'is-placeholder'}`}>
          {value ? selectedLabel : placeholder}
        </span>
        <DownOutlined className="bl-select-caret" />
      </button>

      {open && (
        <ul className="bl-select-menu" role="listbox">
          <li
            role="option"
            aria-selected={!value}
            className={`bl-select-opt ${!value ? 'is-active' : ''}`}
            onClick={() => pick('')}
          >
            <span>{placeholder}</span>
            {!value && <CheckOutlined className="bl-select-check" />}
          </li>
          {items.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
              className={`bl-select-opt ${value === opt.value ? 'is-active' : ''}`}
              onClick={() => pick(opt.value)}
            >
              <span>{opt.label}</span>
              {value === opt.value && <CheckOutlined className="bl-select-check" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default BlSelect;
